import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { authInterceptor } from './core/interceptors/auth.interceptor';
import { chunkLoadErrorHandler } from './core/router/chunk-load-error.handler';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { AuthService } from './core/services/auth.service';
import { AuthSessionService } from './core/services/auth-session.service';
import { LayoutService } from './core/services/layout.service';
import { routes } from './app.routes';

// Angular 20 + Daxa: zone.js change detection + async Material animations.
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
      // A tab left open across a deploy or an `ng serve` restart still points at
      // old lazy-chunk names; the Router then cancels the navigation and the
      // click looks dead. Reload at the target URL to pick up the current build.
      withNavigationErrorHandler(chunkLoadErrorHandler),
    ),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    // Rehydrate the live user from /auth/me on every app load so role-gated
    // navigation and dashboards reflect the *current* user_type — not a stale
    // copy persisted at login. Without this, feature access could lag behind
    // the live profile (e.g. show Client while the account is System Admin).
    // refreshCurrentUser() no-ops without a token and never throws.
    provideAppInitializer(() => inject(AuthService).refreshCurrentUser()),
    // Instantiate the inactivity-timeout watcher at bootstrap so its auth-state
    // effect + activity listeners are active for the whole app lifetime.
    provideAppInitializer(() => {
      inject(AuthSessionService);
    }),
    // Load per-client branding (site_layouts) and apply primary/link colors as
    // CSS variables on <html> at bootstrap. Fails silently → default Daxa theme.
    provideAppInitializer(() => inject(LayoutService).loadBranding()),
  ],
};
